import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';
import Menu from '../Menu/Menu';
import Gravity from '../../constants/Gravity';

/**
 * `ApplicationHeader` is a header container that is typically displayed in the top of an {@link Application}. It
 * provides a page menu, logo, app name, shortcut items as well as user e-mail and number of alerts.
 *
 * The page menu has built in styling for {@link Button} and `a href` elements, but you can add any type of component
 * if you provide your own styling. The page menu has two sections of menu items with different styling. You may add
 * menu items to either section using the props *menuItems* and *secondaryItems* respectively.
 *
 * Adding the class "active" to a menu item will select it the page menu.
 *
 * @property {number} alerts Number of alerts to display. If the number of alerts is greater than 99 the header will
 * display 99+. Defaults to no alerts.
 * @property {string} alertsText The text that will be shown after the alert number, e.g. 11 Alerts. Defaults to
 * "Alerts".
 * @property {string} className The class name of this header. Will be appended to "core-application-header".
 * @property {node} children Custom items you want to add to the application header.
 * @property {node} logo The logo to display in the application header. Can be either a URL string or SVG data. Defaults to
 * the Vimond logo. Also defaults to the Vimond logo if the URL could not be loaded for some reason.
 * @property {node} menuItems The menu items that will be displayed in the primary section of the page menu. These menu
 * items should have icons.
 * @property {function} onClick A callback that is called whenever the user has clicked on the page menu button.
 * @property {node} shortcuts Pinned items are links that are displayed in the center of the application header. They
 * may be links to pinned modules or sections of your application.
 * @property {node} secondaryItems Secondary menu items that will appear in the secondary section of the page menu.
 * These menu items will appear after all main menu items and should not have icons.
 * @property {string} title The title of your application. Defaults to no title.
 * @property {node} user The user element. This will typically be a string or an `a href` element if you want the item
 * to be clickable. Defaults to no user item.
 * @property {string} version A version string that will be displayed after the title on screens >= 480px. Defaults to
 * no version.
 *
 * @see Application, Menu
 */
export default class ApplicationHeader extends PureComponent {

  static displayName = 'ApplicationHeader';

  static propTypes = {
    alerts: PropTypes.number,
    alertsText: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    logo: PropTypes.node,
    menuItems: PropTypes.node,
    onClick: PropTypes.func,
    shortcuts: PropTypes.node,
    secondaryItems: PropTypes.node,
    title: PropTypes.string,
    user: PropTypes.node,
    version: PropTypes.string,
  };

  static defaultProps = {
    alertsText: 'alerts',
  };

  constructor(props) {
    super(props);
    this.state = {};
  }

  onClick = e => this.props.onClick && this.props.onClick(e);

  onCreateMenuItems = () => {
    const { menuItems, secondaryItems } = this.props;

    return [
      <div key="primary" className="core-application-header-menu-content primary" role="primary-menu-items">
        {menuItems}
      </div>,
      <div key="secondary" className="core-application-header-menu-content secondary" role="secondary-menu-items">
        {secondaryItems}
      </div>,
    ];
  };

  onLogoFailedToLoad = () => {
    this.setState({ logoFailedToLoad: true });
  };

  render() {
    const { alerts, alertsText, children, className, onClick, shortcuts, title, user, version } = this.props;
    const bookmarks = shortcuts && [].concat(shortcuts).map((item, index) => <li className="bookmark" key={index}>{item}</li>);
    let logo = this.props.logo;
    let alertsBadge;
    let numberOfAlerts;

    if (logo) {
      if (typeof logo !== 'string' || this.state.logoFailedToLoad) {
        logo = <Icon className="logo" name="vimond" />;
      } else {
        if (logo.charAt(0) === '<') {
          logo = <Icon className="logo" icon={logo} />;
        } else {
          logo = <img className="logo" src={logo} alt="logo" onError={this.onLogoFailedToLoad} />;
        }
      }
    }
    if (alerts > 0) {
      numberOfAlerts = alerts > 99 ? '99+' : alerts;
      alertsBadge = (
        <div className="core-application-header-alerts">
          <span className="core-application-header-alerts-badge">{numberOfAlerts}</span>
          <span className="alerts-text">{alertsText}</span>
        </div>
      );
    }
    return (
      <div className={cx('core-application-header', { 'has-logo': !!logo }, className)}>
        <div className="core-application-header-left">
          <Menu iconName="menu" buttonClass="core-application-header-menu" gravity={Gravity.SOUTH} customContent
            onCreateMenuItems={!onClick ? this.onCreateMenuItems : undefined} onClick={onClick}/>
          {logo}
          <h1>
            {title}
            {version && <span className="version">{version}</span>}
          </h1>
        </div>
        <div className="core-application-header-right">
          {bookmarks}
          {children}
          {alertsBadge}
          {user && <span className="core-application-header-user">{user}</span>}
        </div>
      </div>
    );

  }

}
